/**
 * Tệp trả lời bộ câu hỏi khai báo — xuất ra, điền, tải lên lại.
 *
 * ── ĐỊNH DẠNG CHẶT, ĐỌC BẰNG MÁY, KHÔNG QUA MÔ HÌNH ───────────────────────
 * Bản trước nhận MỌI loại tệp (docx, pdf, xlsx…) rồi gửi từng đoạn 45.000 ký
 * tự cho mô hình suy ra câu trả lời. Với một tệp mà chính màn này xuất ra thì
 * đó là trả tiền mô hình để đọc lại đúng thứ mình vừa viết: tệp 969 câu của
 * nguồn SAP B1 dài 750 KB, cắt thành 17 đoạn, mỗi đoạn một lượt gọi — chậm,
 * tốn, và mô hình vẫn có thể gắn câu trả lời nhầm mã.
 *
 * Nay tệp là một bảng `mã câu → câu trả lời`, đọc tất định. Câu hỏi đi kèm
 * dưới dạng CHÚ THÍCH để người điền đọc được, và bộ đọc bỏ qua chúng.
 *
 * Hai dạng, cùng một nội dung:
 *
 *   YAML (.yml/.yaml) — dạng xuất ra:
 *     dln1.grain: Một dòng là một dòng phiếu giao hàng
 *     dln1.kieu_bang: giao_dich
 *     oinv.nghia_ma.u_trang_thai:
 *       "DPH": Đã phát hành
 *
 *   Markdown (.md) — mọi dòng không bắt đầu bằng "- `" đều bị bỏ qua:
 *     - `dln1.grain`: Một dòng là một dòng phiếu giao hàng
 *     - `oinv.nghia_ma.u_trang_thai`:
 *       - `DPH`: Đã phát hành
 */

import type { CauHoiKhai } from '../types.ts';

export const DUOI_TRA_LOI = '.yml,.yaml,.md';

/** Câu trả lời thô đọc từ tệp: một chuỗi, hoặc ánh xạ mã → nghĩa. */
export type GiaTriTep = string | Record<string, string>;

export interface LoiTep {
  dong: number;
  lyDo: string;
}

export interface KetQuaDocTep {
  traLoi: Map<string, GiaTriTep>;
  loi: LoiTep[];
}

// ── XUẤT ──────────────────────────────────────────────────────────────────

const HAU_QUA: Record<string, string> = {
  sai_am_tham: 'trả lời SAI mà không ai biết',
  bi_tu_choi: 'bị từ chối, người dùng thấy',
  kho_doc: 'chỉ khó đọc',
};

function motDong(s: string): string {
  return (s || '').replace(/\s*\n\s*/g, ' ').trim();
}

/** Khoá YAML luôn đặt trong nháy kép: mã có thể là số (`02`), có dấu `:` hay `#`. */
function khoaYml(s: string): string {
  return JSON.stringify(s);
}

function chuThichCau(q: CauHoiKhai): string[] {
  const d = [`# ${motDong(q.noiDung)} (${HAU_QUA[q.hauQua] ?? q.hauQua})`];
  const chon = q.luaChon.filter((c) => c.giaTri);
  if (!q.nhieuLuaChon && !q.traLoiTuDo && chon.length) {
    const ds = chon.map((c) =>
      c.nhan && c.nhan !== c.giaTri ? `${c.giaTri} (${c.nhan})` : c.giaTri,
    );
    d.push(`# Chọn một: ${ds.join(' | ')}`);
  }
  if (q.goiY?.giaTri) d.push(`# Máy đoán: ${motDong(q.goiY.giaTri)}`);
  return d;
}

function motCauYml(q: CauHoiKhai): string[] {
  const d = chuThichCau(q);
  if (q.nhieuLuaChon) {
    d.push(`${q.ma}:`);
    for (const c of q.luaChon) {
      const bc = c.bangChung ? `  # ${motDong(c.bangChung)}` : '';
      d.push(`  ${khoaYml(c.giaTri)}:${bc}`);
    }
  } else {
    d.push(`${q.ma}:`);
  }
  d.push('');
  return d;
}

/** Tệp YAML điền được: mỗi câu là một dòng `mã:`, câu hỏi nằm ở chú thích. */
export function taoYmlCauHoi(
  cauHoi: CauHoiKhai[],
  nhanBang: Record<string, string>,
  tenNguon: string,
): string {
  const d: string[] = [
    `# Bản khai báo nghiệp vụ — nguồn ${tenNguon} — ${cauHoi.length} câu`,
    '#',
    '# Điền câu trả lời sau dấu ":" của từng mã. Bỏ trống câu nào không biết.',
    '# Câu có "Chọn một": ghi đúng một giá trị trong danh sách, ví dụ: co',
    '# Câu nghĩa mã: điền nghĩa sau dấu ":" của từng mã ở dòng thụt vào.',
    '# Dòng bắt đầu bằng "#" là chú thích — sửa hay xoá đều được.',
    '# Tải lên lại ở nút "Tải tệp trả lời". Câu trả lời hiện ra dạng ĐỀ XUẤT, vẫn phải Duyệt.',
    '',
  ];
  const theoBang = new Map<string, CauHoiKhai[]>();
  for (const q of cauHoi) theoBang.set(q.bang, [...(theoBang.get(q.bang) ?? []), q]);
  for (const [ten, cs] of theoBang) {
    const nhan = nhanBang[ten];
    d.push(`# ═══ ${ten}${nhan && nhan !== ten ? ` — ${nhan}` : ''}`, '');
    for (const q of cs) d.push(...motCauYml(q));
  }
  return d.join('\n');
}

export function tenTepCauHoi(sourceId: string): string {
  return `ban-khai-${sourceId}-${new Date().toISOString().slice(0, 10)}.yml`;
}

// ── ĐỌC ───────────────────────────────────────────────────────────────────

/** Giá trị vô hướng YAML: nháy kép (JSON), nháy đơn (`''` = `'`), hoặc trơn. */
function giaTriVoHuong(tho: string): string | null {
  const s = tho.trim();
  // Chỉ có chú thích — đúng dạng tệp xuất ra với mã chưa điền: `"DPH":  # 137 dòng`.
  if (s.startsWith('#')) return '';
  if (s.startsWith('"')) {
    const m = /^("(?:[^"\\]|\\.)*")\s*(?:#.*)?$/.exec(s);
    try {
      const v: unknown = m?.[1] ? JSON.parse(m[1]) : null;
      return typeof v === 'string' ? v : null;
    } catch {
      return null;
    }
  }
  if (s.startsWith("'")) {
    const m = /^'((?:[^']|'')*)'\s*(?:#.*)?$/.exec(s);
    return m ? (m[1] ?? '').replace(/''/g, "'") : null;
  }
  // Chú thích cuối dòng của YAML: " #" (có khoảng trắng trước) — `#` dính liền
  // chữ vẫn là một phần giá trị, đúng như YAML đọc.
  const i = s.search(/\s#/);
  return (i < 0 ? s : s.slice(0, i)).trim();
}

/** Tách `khoá: phần còn lại`. Khoá được đặt trong nháy kép hoặc viết trơn. */
function tachKhoa(s: string): { khoa: string; con: string } | null {
  const m = s.startsWith('"')
    ? /^("(?:[^"\\]|\\.)*")\s*:(.*)$/.exec(s)
    : /^([^:#\s][^:]*?)\s*:(.*)$/.exec(s);
  if (!m?.[1]) return null;
  const khoa = m[1].startsWith('"') ? giaTriVoHuong(m[1]) : m[1].trim();
  return khoa ? { khoa, con: m[2] ?? '' } : null;
}

function ghiVao(traLoi: Map<string, GiaTriTep>, ma: string, v: GiaTriTep) {
  const rong = typeof v === 'string' ? !v : !Object.keys(v).length;
  if (!rong) traLoi.set(ma, v);
}

export function docYml(van: string): KetQuaDocTep {
  const traLoi = new Map<string, GiaTriTep>();
  const loi: LoiTep[] = [];
  let ma: string | null = null;
  let cap: Record<string, string> = {};
  const dong = () => {
    if (ma) ghiVao(traLoi, ma, cap);
    ma = null;
    cap = {};
  };
  van.split(/\r?\n/).forEach((raw, i) => {
    if (!raw.trim() || raw.trim().startsWith('#')) return;
    const thut = /^\s/.test(raw);
    // Dòng hỏng ở cấp ngoài cũng khép mã câu đang mở — không thì dòng thụt vào
    // ngay sau nó bị gán nhầm vào mã câu phía trên.
    if (!thut) dong();
    const kv = tachKhoa(raw.trim());
    if (!kv) return void loi.push({ dong: i + 1, lyDo: 'không đúng dạng "khoá: giá trị"' });
    const v = giaTriVoHuong(kv.con);
    if (v === null) return void loi.push({ dong: i + 1, lyDo: 'giá trị trong nháy bị hỏng' });
    if (thut) {
      if (!ma) return void loi.push({ dong: i + 1, lyDo: 'dòng thụt vào không thuộc mã câu nào' });
      if (v) cap[kv.khoa] = v;
      return;
    }
    if (v) ghiVao(traLoi, kv.khoa, v);
    else ma = kv.khoa;
  });
  dong();
  return { traLoi, loi };
}

export function docMd(van: string): KetQuaDocTep {
  const traLoi = new Map<string, GiaTriTep>();
  const loi: LoiTep[] = [];
  let ma: string | null = null;
  let cap: Record<string, string> = {};
  const dong = () => {
    if (ma) ghiVao(traLoi, ma, cap);
    ma = null;
    cap = {};
  };
  van.split(/\r?\n/).forEach((raw, i) => {
    const m = /^(\s*)- `([^`]+)`\s*[:=](.*)$/.exec(raw);
    if (!m) {
      if (/^- `/.test(raw)) dong();
      if (/^\s*- `/.test(raw)) loi.push({ dong: i + 1, lyDo: 'thiếu ":" sau mã' });
      return;
    }
    const [, thut = '', khoa = '', con = ''] = m;
    const v = con.trim();
    if (thut) {
      if (!ma) return void loi.push({ dong: i + 1, lyDo: 'dòng thụt vào không thuộc mã câu nào' });
      if (v) cap[khoa] = v;
      return;
    }
    dong();
    if (v) ghiVao(traLoi, khoa, v);
    else ma = khoa;
  });
  dong();
  return { traLoi, loi };
}

// ── ĐỐI CHIẾU VỚI BỘ CÂU HỎI ───────────────────────────────────────────────

export interface DeXuatTuTep {
  ma: string;
  giaTri: string;
  cap?: Record<string, string>;
}

export interface KetQuaDoiChieu {
  hopLe: DeXuatTuTep[];
  loi: { ma: string; lyDo: string }[];
}

/**
 * Kiểm từng câu trả lời theo đúng hình dạng câu hỏi. Không sửa, không đoán:
 * giá trị ngoài danh sách lựa chọn là LỖI, không phải "gần đúng" — đoán gần
 * đúng chính là việc bộ đọc này sinh ra để thôi làm.
 */
export function doiChieu(traLoi: Map<string, GiaTriTep>, cauHoi: CauHoiKhai[]): KetQuaDoiChieu {
  const theoMa = new Map(cauHoi.map((q) => [q.ma, q]));
  const hopLe: DeXuatTuTep[] = [];
  const loi: KetQuaDoiChieu['loi'] = [];
  for (const [ma, v] of traLoi) {
    const q = theoMa.get(ma);
    if (!q) {
      loi.push({ ma, lyDo: 'mã câu không có trong bộ câu hỏi đang còn trống' });
    } else if (q.nhieuLuaChon) {
      if (typeof v === 'string') loi.push({ ma, lyDo: 'câu nghĩa mã cần từng dòng "mã: nghĩa"' });
      else hopLe.push({ ma, giaTri: '', cap: v });
    } else if (typeof v !== 'string') {
      loi.push({ ma, lyDo: 'câu này cần MỘT giá trị, không phải danh sách' });
    } else if (!q.traLoiTuDo && q.luaChon.length) {
      const khop = q.luaChon.find(
        (c) => c.giaTri.toLowerCase() === v.replace(/`/g, '').toLowerCase(),
      );
      if (khop) hopLe.push({ ma, giaTri: khop.giaTri });
      else
        loi.push({
          ma,
          lyDo: `"${v}" không nằm trong: ${q.luaChon.map((c) => c.giaTri).join(' | ')}`,
        });
    } else {
      hopLe.push({ ma, giaTri: v });
    }
  }
  return { hopLe, loi };
}

/** Báo cáo lỗi để tải về khi tệp có dòng không đọc được — nói đúng dòng, đúng mã. */
export function taoMdLoi(tenTep: string, loiDong: LoiTep[], loiMa: KetQuaDoiChieu['loi']): string {
  const d = [`# Lỗi đọc tệp trả lời — ${tenTep}`, ''];
  if (loiDong.length) {
    d.push('## Dòng sai định dạng', '');
    for (const l of loiDong) d.push(`- Dòng ${l.dong}: ${l.lyDo}`);
    d.push('');
  }
  if (loiMa.length) {
    d.push('## Câu trả lời bị bỏ', '');
    for (const l of loiMa) d.push(`- \`${l.ma}\`: ${l.lyDo}`);
  }
  return d.join('\n');
}
